import React, { useEffect, useState, useContext } from "react";
import {
  Button,
  Empty,
  message,
  Popconfirm,
  Table,
  Tooltip,
  Col,
  Row,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import {
  LoadingOutlined,
  UserSwitchOutlined,
  UnlockOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { translate } from "../../locale";
import UserServices from "../../services/UserServices";
import { useParams } from "react-router-dom";
import ChangeRole from "../Modals/ChangeRole";
import { TableHelper } from "../../helpers/TableHelper";
import { AdminContext } from "../../providers/AdminContext";

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const AdministratorUsers = ({ reload, enterpriseId, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(false);
  const { id } = useParams();
  const [showChangeRole, setShowChangeRole] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loadingAllow, setLoadingAllow] = useState(false);
  const [loadingDisallow, setLoadingDisallow] = useState(false);
  const hasSelected = selectedRowKeys.length > 0;
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);

  const [isSuper, setIsSuper] = useState(false);
  const { user, setProfile } = useContext(AdminContext);

  useEffect(() => {
    if (user !== null && user !== undefined) {
      setIsSuper(user.user.rol === "eira-super-admin");
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (reload > 0) {
      loadData();
    }
  }, [reload]);

  const loadData = async () => {
    let data = {
      page_size: tableParams.pagination.pageSize,
      page_num: tableParams.pagination.current,
    };
    const sorter = TableHelper.sorter(tableParams);
    if (sorter !== null) {
      data = { ...data, order_by: sorter };
    }
    setLoading(true);
    try {
      const response = await UserServices.getManagement(id, data);
      if (response.status >= 200 && response.status < 300) {
        setData(response.data.management);
        setTotal(response.data.total_pages * tableParams.pagination.pageSize);
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleDelete = async (data) => {
    setLoading(true);
    try {
      const response = await UserServices.deleteManagement(data.id);
      if (response.status >= 200 && response.status < 300) {
        message.success(
          translate(
            "El {0} se guardó exitosamente.",
            translate("usuario administrador")
          )
        );
        loadData();
      } else {
        message.error(response.data.message);
      }
      setLoading(false);
    } catch (e) {
      console.log(e);
      message.error(e.message);
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex space-x-4 justify-center">
          <Popconfirm
            title={translate("¿Estás seguro de eliminar el registro?")}
            okText={translate("Si")}
            cancelText={translate("No")}
            onConfirm={() => handleDelete(record)}
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              className="border-0"
            />
          </Popconfirm>

          <Tooltip title={translate("Cambiar rol")}>
            <Button
              type="text"
              icon={<UserSwitchOutlined />}
              size="small"
              className="border-0"
              onClick={() => {
                setShowChangeRole({
                  ...record,
                  user: { rol: "eira-company-admin" },
                });
              }}
            />
          </Tooltip>
        </div>
      ),
      width: 100,
    },
    {
      title: translate("Usuario"),
      dataIndex: "username",
      key: "username",
      render: (_, record) => record.manager.username,
      sorter: (a, b) => {
        if (a.manager.username < b.manager.username) return -1;
        if (a.manager.username > b.manager.username) return 1;
        return 0;
      },
    },
    {
      title: translate("Nombre Completo"),
      dataIndex: "full_name",
      key: "full_name",
      render: (_, record) => record.manager.full_name,
      sorter: (a, b) => {
        if (a.manager.full_name < b.manager.full_name) return -1;
        if (a.manager.full_name > b.manager.full_name) return 1;
        return 0;
      },
    },
    {
      title: translate("Recibir Notificaciones"),
      dataIndex: "can_invite",
      key: "can_invite",
      render: (value, record, index) =>
        translate(record.receive_email_new_users ? "Si" : "No"),
      sorter: (a, b) => {
        if (a.receive_email_new_users < b.receive_email_new_users) return -1;
        if (a.receive_email_new_users > b.receive_email_new_users) return 1;
        return 0;
      },
    },
  ];

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleInvitations = (allow) => {
    if (allow) {
      setLoadingAllow(true);
    } else {
      setLoadingDisallow(true);
    }
    try {
      const _data = {
        manager: "",
        receive_email_new_users: allow,
        enterprise: enterpriseId,
      };
      const inviteResponse = selectedRowKeys.map((username) =>
        UserServices.recieveEmailNewUsers({ ..._data, manager: username })
      );
      Promise.allSettled(inviteResponse).then((response) => {
        const allRs = response
          .map((rs) => {
            if (rs.status === "fulfilled" && rs.value.status >= 300) {
              const data = JSON.parse(rs.value.config.data);
              return data.username;
            }
          })
          .filter((f) => f !== undefined);
        if (allRs.length > 0) {
          message.error(
            `No se pudieron cambiar los permisos de los siguientes usuarios (${allRs.join(
              ", "
            )})`
          );
        } else {
          message.success(
            translate(`Los permisos han sido actualizados correctamente`)
          );
        }
        setSelectedRowKeys([]);
      });
      setLoading(false);
    } catch (e) {
      console.log(e);
      message.error(e.message);
    } finally {
      setSelectedRowKeys([]);
      if (allow) {
        setLoadingAllow(false);
      } else {
        setLoadingDisallow(false);
      }
      setTimeout(loadData, 100);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  const onChangeRoleComplete = () => {
    setShowChangeRole(false);
    if (onChange) {
      onChange();
    }
  };

  return (
    <Row gutter={16}>
      <Col span="24" className="space-x-4 mb-2">
        <Tooltip title={translate("Recibir notificaciones")}>
          <Button
            type="primary"
            onClick={() => handleInvitations(true)}
            disabled={
              !hasSelected ||
              loadingAllow ||
              (user.user.username != selectedRowKeys && !isSuper)
            }
            loading={loadingAllow}
            icon={<UnlockOutlined />}
            ghost
          />
        </Tooltip>
        <Tooltip title={translate("No recibir notificaciones")}>
          <Button
            type="primary"
            onClick={() => handleInvitations(false)}
            disabled={
              !hasSelected ||
              loadingAllow ||
              (user.user.username != selectedRowKeys && !isSuper)
            }
            loading={loadingDisallow}
            icon={<LockOutlined />}
            ghost
          />
        </Tooltip>
        <span style={{ marginLeft: 8 }}>
          {hasSelected
            ? translate(`{0} registros seleccionados`, selectedRowKeys.length)
            : ""}
        </span>
      </Col>
      <Col span="24">
        <Table
          rowKey={(i) => i.manager.username}
          rowSelection={rowSelection}
          dataSource={data}
          columns={columns}
          loading={
            loading
              ? { indicator: antIcon, className: "text-custom_blue" }
              : false
          }
          locale={{
            emptyText: <Empty description={translate("No hay datos")} />,
            sortTitle: translate("Ordenar"),
            triggerDesc: translate("Ordenar (descendente)"),
            triggerAsc: translate("Ordenar (ascendente)"),
            cancelSort: translate("Cancelar orden"),
          }}
          pagination={{ ...tableParams.pagination, total: total }}
          onChange={handleTableChange}
        />
      </Col>
      <ChangeRole
        data={showChangeRole}
        onComplete={onChangeRoleComplete}
        onCancel={() => setShowChangeRole(false)}
        enterpriseId={id}
      />
    </Row>
  );
};

export default AdministratorUsers;
